import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import './Ceremonies.css';

interface Participant {
    name: string;
    mobile: string;
    details?: string;
    mediaUrl?: string;
}

interface Ceremony {
    _id: string;
    name: string;
    date: string;
    time: string;
    venue: string;
    dressCode?: string;
    description?: string;
    participants?: Participant[];
}

const GuestSchedule: React.FC = () => {
    const { accessToken, loading: authLoading } = useAuth();
    const history = useHistory();
    const [ceremonies, setCeremonies] = useState<Ceremony[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/ceremonies`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = await res.json();
            if (res.ok) {
                setCeremonies(data.ceremonies);
            }
        } catch (err) {
            console.error('Error fetching schedule:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading) {
            if (accessToken) {
                fetchData();
            } else {
                history.push('/login');
            }
        }
    }, [accessToken, authLoading, history]);

    if (authLoading || loading) return <div className="ceremonies-container">Loading schedule...</div>;

    return (
        <div className="guest-feed-page ceremonies-container">
            <header className="feed-header">
                <div className="header-content">
                    <h1>Wedding Schedule</h1>
                    <p>Mark your calendars for these special moments</p>
                </div>
            </header>

            <main className="feed-container">
                {ceremonies.length === 0 ? (
                    <div className="empty-feed">
                        <span className="empty-icon">🗓️</span>
                        <h3>No ceremonies scheduled yet</h3>
                        <p>Stay tuned for the official itinerary!</p>
                    </div>
                ) : (
                    <div className="ceremonies-grid">
                        {ceremonies.map((c) => (
                            <div key={c._id} className="ceremony-card">
                                <div className="ceremony-card-header">
                                    <h2 className="ceremony-name">{c.name}</h2>
                                </div>
                                <div className="ceremony-info">
                                    <div className="info-item">
                                        <span className="info-icon">📅</span>
                                        <div className="info-content">
                                            <span className="info-label">Date</span>
                                            <span className="info-text">{new Date(c.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-icon">⏰</span>
                                        <div className="info-content">
                                            <span className="info-label">Time</span>
                                            <span className="info-text">{c.time}</span>
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-icon">📍</span>
                                        <div className="info-content">
                                            <span className="info-label">Venue</span>
                                            <span className="info-text">{c.venue}</span>
                                        </div>
                                    </div>
                                    {c.dressCode && (
                                        <div className="info-item">
                                            <span className="info-icon">👔</span>
                                            <div className="info-content">
                                                <span className="info-label">Dress Code</span>
                                                <span className="info-text">{c.dressCode}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {c.description && (
                                    <div className="ceremony-description" style={{ marginTop: '1.5rem', fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: '1.6' }}>
                                        <p>{c.description}</p>
                                    </div>
                                )}

                                {c.participants && c.participants.length > 0 && (
                                    <div className="participants-section" style={{ marginTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '1.5rem' }}>
                                        <h4 style={{ fontSize: '0.9rem', color: 'var(--primary-accent)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span>👥</span> Key Participants
                                        </h4>
                                        <div style={{ display: 'grid', gap: '10px' }}>
                                            {c.participants.map((p, i) => (
                                                <div key={i} style={{ background: 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '12px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <span style={{ fontWeight: '600' }}>{p.name}</span>
                                                    </div>
                                                    {p.details && <p style={{ fontSize: '0.8rem', margin: '5px 0 0', color: 'var(--text-muted)' }}>{p.details}</p>}
                                                    {p.mediaUrl && (
                                                        <div style={{ marginTop: '10px' }}>
                                                            <audio src={p.mediaUrl} controls style={{ width: '100%', height: '30px' }} />
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default GuestSchedule;

